import React from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import { YStack, XStack, Separator, useMedia } from 'tamagui'

import { UserCard } from '@newComponents/User/UserCard'
import { Grid } from '@newComponents/Grid'
import { Button } from '@newComponents/Button/Button'
import { H2 } from '@newComponents/Text'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersEditorsSection_guild$key } from './__generated__/GuildMembersEditorsSection_guild.graphql'

function useItemsPerRow() {
  const media = useMedia()
  if (media.xs) return INITIAL_COUNTS[0]
  if (media.sm) return INITIAL_COUNTS[1]
  if (media.md) return INITIAL_COUNTS[2]
  if (media.lg) return INITIAL_COUNTS[3]
  return INITIAL_COUNTS[4]
}

export const GuildMembersEditorsSection = ({
  guild,
}: {
  guild: GuildMembersEditorsSection_guild$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersEditorsSection_guild on Guild
      @refetchable(queryName: "GuildMembersEditorsSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 3 }
        cursor: { type: "Cursor" }
      ) {
        editors(first: $count, after: $cursor)
          @connection(key: "GuildMembersEditorsSection_guild_editors") {
          edges {
            node {
              ...UserCardNew_user
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const itemsPerRow = useItemsPerRow()
  const media = useMedia()

  if (data.editors.edges.length === 0) {
    return null
  }

  if (media.xs) {
    return (
      <YStack gap='$4'>
        <XStack gap='$4' alignItems='center'>
          <H2 size='$4' color='$colorMuted' fontWeight='$5'>
            Editors
          </H2>
          <Separator flex={1} borderColor='$borderColor' />
        </XStack>

        {data.editors.edges.map((edge) => (
          <UserCard
            key={edge.cursor}
            user={edge.node}
            limitDescriptionLines={4}
          />
        ))}

        {hasNext && (
          <Button
            secondary
            onPress={() => {
              if (isLoadingNext) return
              loadNext(3)
            }}
          >
            <Button.Text secondary>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Button.Text>
          </Button>
        )}
      </YStack>
    )
  }

  const editorGroups = data.editors.edges.reduce<
    Array<Array<(typeof data.editors.edges)[number]>>
  >((memo, organizer, index) => {
    if (index % itemsPerRow === 0) {
      memo.push([organizer])
    } else {
      memo[memo.length - 1].push(organizer)
    }

    return memo
  }, [])

  return (
    <YStack gap='$4'>
      <XStack gap='$4' alignItems='center'>
        <H2 size='$4' color='$colorMuted' fontWeight='$5'>
          Editors
        </H2>
        <Separator flex={1} borderColor='$borderColor' />
      </XStack>

      <YStack gap='$4'>
        {editorGroups.map((group, index) => {
          return (
            <XStack key={index}>
              <Grid gap='$4'>
                {group.map((edge) => (
                  <Grid.Column key={edge.cursor}>
                    <UserCard
                      user={edge.node}
                      limitDescriptionLines={4}
                      expand
                    />
                  </Grid.Column>
                ))}

                {Array.from({
                  length: itemsPerRow - group.length,
                }).map((_, i) => (
                  <Grid.Column key={i} />
                ))}
              </Grid>
            </XStack>
          )
        })}
      </YStack>

      {hasNext && (
        <Button
          secondary
          onPress={() => {
            if (isLoadingNext) return
            loadNext(3)
          }}
        >
          <Button.Text secondary>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Button.Text>
        </Button>
      )}
    </YStack>
  )
}
